import React, { useState, useContext, useEffect } from 'react';
import i18next from 'i18next';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';
import { FormLabel, Grid, TextField, Tooltip, Typography } from '@mui/material';
import AdditionalMessageAlert from 'components/AdditionalMessageAlert';
import EAChoiceSeparator from 'components/EAChoiceSeparator';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import EcovadisDialogButtons from '../EcovadisDialogButtons';
import EAIcon from 'components/common/EAIcon';
import EAPhoneInput from 'components/inputs/EAPhoneInput';
import { Context } from 'states/Store';
import { REGEXP_EMAIL } from 'services/common/Constants';
import EAAutoCompleteInput from 'components/inputs/EAAutoCompleteInput';

const SupplierTooltip = () => {
  return (
    <>
      {i18next.t('ecovadis.requestForm.contactStep.supplier.tooltip1')}
      {i18next.t('ecovadis.requestForm.contactStep.supplier.tooltip2')}
    </>
  );
};

const EcovadisEvaluationSupplierStep = ({
  onCancel = () => {},
  onValidate = () => {},
}) => {
  const { classes } = useStyles();

  const [state] = useContext(Context);
  const [thirdpartyContacts, setThirdpartyContacts] = useState([]);

  const {
    control,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext();

  let supplierId = useWatch({ name: 'supplierId' });

  useEffect(() => {
    if (state.thirdpartyContacts) {
      setThirdpartyContacts(
        state.thirdpartyContacts.map((contact) => ({
          value: contact.id,
          label: `${contact.firstname} ${contact.name}`,
        }))
      );
    } else {
      setThirdpartyContacts([]);
    }
  }, [state.thirdpartyContacts]);

  useEffect(() => {
    const selectedContact = state.thirdpartyContacts?.find(
      (x) => x.id === supplierId
    );
    if (selectedContact) {
      clearErrors();
      setValue('supplier_contact_first_name', selectedContact.firstname || '');
      setValue('supplier_contact_last_name', selectedContact.name || '');
      const trimmedPhone = selectedContact.phone?.startsWith('+')
        ? selectedContact.phone.substring(1)
        : selectedContact.phone;
      setValue('supplier_contact_phone', trimmedPhone || '');
      setValue('supplier_contact_email', selectedContact.email || '');
    }
  }, [supplierId, state.thirdpartyContacts, clearErrors, setValue]);

  return (
    <div className={classes.integrationIdStep}>
      <AdditionalMessageAlert
        icon="lightbulb"
        message={i18next.t('ecovadis.requestForm.contactStep.supplier.tip')}
      />
      <Typography className={classes.stepTitle}>
        {i18next.t('ecovadis.requestForm.contactStep.supplier.subTitle')}
        <Tooltip arrow title={<SupplierTooltip />}>
          <span className={classes.stepTitleTooltip}>
            <EAIcon icon={'info-rounded'} />
          </span>
        </Tooltip>
      </Typography>

      <Grid container>
        <Grid item container>
          <Grid item xs={4} className={classes.choiceBlocLabel}>
            <EAIcon icon={'search'} />
            {i18next.t('ecovadis.requestForm.contactStep.chooseFromList')}
          </Grid>
          <Grid item container xs={8} className={classes.formContainer}>
            <Grid item xs={12} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t(
                  'ecovadis.requestForm.contactStep.supplier.supplierListLabel'
                )}
              </FormLabel>
              <EAAutoCompleteInput
                fieldName={'supplierId'}
                options={thirdpartyContacts}
                placeholder={i18next.t(
                  'ecovadis.requestForm.contactStep.supplier.supplierListPlaceholder'
                )}
                variant="outlined"
                limitTags={1}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={4}></Grid>
          <Grid item xs={8}>
            <div className={classes['separator-horizontal']}>
              <EAChoiceSeparator mode="horizontal" fullSize />
            </div>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={4} className={classes.choiceBlocLabel}>
            <EAIcon icon={'edit'} />
            <span>
              {i18next.t('ecovadis.requestForm.contactStep.manualInvitation')}
              <div className={classes.choiceBlocSubLabel}>
                {i18next.t('ecovadis.requestForm.contactStep.mandatoryFields')}
              </div>
            </span>
          </Grid>
          <Grid item container xs={8} className={classes.formContainer}>
            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('firstName')}
              </FormLabel>

              <Controller
                control={control}
                name="supplier_contact_first_name"
                rules={{
                  required: i18next.t('infos.required'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="supplier_contact_first_name"
                    variant="outlined"
                    error={!!errors.supplier_contact_first_name}
                    helperText={
                      !!errors.supplier_contact_first_name
                        ? errors.supplier_contact_first_name.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('lastName')}
              </FormLabel>

              <Controller
                control={control}
                name="supplier_contact_last_name"
                rules={{
                  required: i18next.t('infos.required'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="supplier_contact_last_name"
                    variant="outlined"
                    error={!!errors.supplier_contact_last_name}
                    helperText={
                      !!errors.supplier_contact_last_name
                        ? errors.supplier_contact_last_name.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('phone')}
              </FormLabel>
              <EAPhoneInput name="supplier_contact_phone" />
            </Grid>
            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('email')}
              </FormLabel>

              <Controller
                control={control}
                name="supplier_contact_email"
                rules={{
                  required: i18next.t('infos.required'),
                  pattern: {
                    value: REGEXP_EMAIL,
                    message: i18next.t('infos.USER_EMAIL_ERROR'),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="supplier_contact_email"
                    variant="outlined"
                    error={!!errors.supplier_contact_email}
                    helperText={
                      !!errors.supplier_contact_email
                        ? errors.supplier_contact_email.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <EcovadisDialogButtons onCancel={onCancel} onValidate={onValidate} />
    </div>
  );
};

export default EcovadisEvaluationSupplierStep;
